/**
 * Privacy classes drive sync eligibility, redaction posture, and AI/notification
 * routing. They are mandatory on every persisted record and every system.
 */
export type PrivacyClass =
  | 'device_secret'
  | 'strict_local'
  | 'sensitive_private'
  | 'standard_private'
  | 'public_share'
  | 'finance_vault';

export const PRIVACY_CLASSES: readonly PrivacyClass[] = [
  'device_secret',
  'strict_local',
  'sensitive_private',
  'standard_private',
  'public_share',
  'finance_vault',
] as const;

/**
 * Local-only classes never leave the device. This is the single source of
 * truth for the "strict-local never syncs" invariant.
 */
export function isLocalOnly(privacyClass: PrivacyClass): boolean {
  return privacyClass === 'strict_local' || privacyClass === 'device_secret';
}

/** Whether a record of this class may ever be uploaded to cloud sync. */
export function isSyncEligible(privacyClass: PrivacyClass): boolean {
  return !isLocalOnly(privacyClass);
}

/**
 * Default redaction posture for any delivery channel (notification, bridge).
 * Only clearly non-sensitive classes are shown unredacted by default.
 */
export function redactByDefault(privacyClass: PrivacyClass): boolean {
  return !(privacyClass === 'public_share' || privacyClass === 'standard_private');
}

/** Whether content of this class may be sent to a hosted AI provider by default. */
export function aiEligibleByDefault(privacyClass: PrivacyClass): boolean {
  return privacyClass === 'standard_private' || privacyClass === 'public_share';
}

export function isPrivacyClass(value: unknown): value is PrivacyClass {
  return typeof value === 'string' && (PRIVACY_CLASSES as readonly string[]).includes(value);
}
